<template>
  <v-dialog v-model="dialog" transition="dialog-bottom-transition" scrollable max-width="560">
    <template v-if="!hideButton" v-slot:activator="{ props }">
      <v-btn
        color="primary"
        :class="{ 'float-right': floatRight, 'mx-2': floatRight }"
        v-bind="props"
      >
        <v-icon class="mr-2">mdi-link-variant-plus</v-icon>
        Vincular produto
      </v-btn>
    </template>
    <v-card>
      <v-toolbar color="primary" :title="toolbarTitle">
        <v-btn color="black" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              v-model="form.product_id"
              v-model:search="productSearch"
              :items="productItems"
              item-title="title"
              item-value="value"
              label="Produto (com estoque no grupo)"
              :rules="[(v) => !!v || 'Campo obrigatório']"
              :loading="loadingProducts"
              :disabled="requestingApi || isEdit"
              :custom-filter="filterProduct"
              density="compact"
              clearable
              hide-details="auto"
              :no-data-text="productNoDataText"
              hint="Só produtos com saldo disponível no estoque do grupo. Digite para filtrar por nome ou SKU."
              persistent-hint
            >
              <template v-slot:item="{ props: itemProps, item }">
                <v-list-item v-bind="itemProps" :title="item.raw.title">
                  <template v-slot:subtitle>
                    <span v-if="item.raw.sku">SKU {{ item.raw.sku }} · </span>
                    {{ item.raw.stockLabel }}
                  </template>
                </v-list-item>
              </template>
              <template v-slot:no-data>
                <div class="px-4 py-2 text-medium-emphasis">
                  {{ productNoDataText }}
                </div>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.price_override"
              label="Preço override"
              type="number"
              min="0"
              step="0.01"
              clearable
              density="compact"
              :disabled="requestingApi"
              :hint="priceOverrideHint"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.pdv_sort"
              label="Ordem PDV"
              type="number"
              density="compact"
              :disabled="requestingApi"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-switch
              color="green"
              :label="form.active ? 'Ativo' : 'Inativo'"
              v-model="form.active"
              :disabled="requestingApi"
              hide-details
            ></v-switch>
          </v-col>
          <v-col v-if="showStockHint" cols="12">
            <v-alert type="info" variant="tonal" density="compact" class="mb-0">
              Listagem só inclui produtos com <strong>saldo no grupo</strong> (variação com
              disponível &gt; 0). Depois aloque claim nesta loja. Produtos já vinculados que
              zerarem no PDV não precisam ser desvinculados.
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn variant="text" color="error" @click="dialog = false">Cancelar</v-btn>
        <v-btn
          color="success"
          :loading="requestingApi"
          :disabled="requestingApi"
          @click="submit"
        >
          <v-icon class="mr-2">mdi-content-save</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import api from '@/services'
import { normalizeListPayload } from '@/utils/listPayload'

function emptyForm(storeId) {
  return {
    id: null,
    store_id: storeId != null ? Number(storeId) : null,
    product_id: null,
    price_override: null,
    active: true,
    pdv_sort: 0,
    initial_quantity: null,
    product: null,
  }
}

function formatQty(n) {
  const x = Number(n)
  if (!Number.isFinite(x)) return '0'
  return x.toLocaleString('pt-BR')
}

export default {
  props: {
    user: Object,
    storeId: {
      type: [Number, String],
      required: true,
    },
    modelValue: Object,
    floatRight: Boolean,
    hideButton: Boolean,
    /** Product ids already linked to this store (hidden on create). */
    excludeProductIds: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'created', 'updated'],
  data() {
    return {
      dialog: false,
      requestingApi: false,
      loadingProducts: false,
      products: [],
      /** product_id → { qty, variantCount } from group balances with available > 0 */
      stockByProductId: {},
      productSearch: '',
      form: emptyForm(this.storeId),
    }
  },
  computed: {
    isEdit() {
      return !!(this.form?.id || this.modelValue?.id)
    },
    toolbarTitle() {
      return this.isEdit ? 'Editar produto da loja' : 'Vincular produto'
    },
    excludedSet() {
      const set = new Set()
      for (const id of this.excludeProductIds || []) {
        if (id != null) set.add(Number(id))
      }
      return set
    },
    productItems() {
      const stockMap = this.stockByProductId || {}
      let list = this.products || []

      if (!this.isEdit) {
        // Only products with positive group stock on at least one variant
        list = list.filter((p) => {
          const s = stockMap[p.id]
          return s && s.qty > 0
        })
        // Already linked — no need to re-offer on create
        if (this.excludedSet.size) {
          list = list.filter((p) => !this.excludedSet.has(Number(p.id)))
        }
      }

      const items = list.map((p) => {
        const s = stockMap[p.id]
        const qty = s?.qty ?? 0
        const variants = s?.variantCount ?? 0
        let stockLabel = `${formatQty(qty)} disp. no grupo`
        if (variants > 1) {
          stockLabel += ` · ${variants} var.`
        }
        const name = p.name || `Produto #${p.id}`
        return {
          title: name,
          value: p.id,
          product: p,
          sku: p.sku || '',
          stockLabel,
          searchText: [name, p.sku, stockLabel].filter(Boolean).join(' ').toLowerCase(),
        }
      })

      // Editing: ensure current product appears even with zero stock / not in picker
      if (this.isEdit) {
        const current = this.form?.product || this.modelValue?.product
        if (current?.id && !items.some((i) => i.value === current.id)) {
          items.unshift({
            title: current.name || `Produto #${current.id}`,
            value: current.id,
            product: current,
            sku: current.sku || '',
            stockLabel: 'já vinculado',
            searchText: String(current.name || '').toLowerCase(),
          })
        }
      }
      return items
    },
    selectedProduct() {
      const id = this.form?.product_id
      if (!id) return this.form?.product || null
      const fromList = (this.products || []).find((p) => p.id === id)
      if (fromList) return fromList
      return this.form?.product || this.modelValue?.product || null
    },
    productHasVariants() {
      const p = this.selectedProduct
      if (!p) return false
      if (p.has_variants === true) return true
      const count = Number(p.variants_count)
      return Number.isFinite(count) && count > 1
    },
    showStockHint() {
      return !this.isEdit
    },
    priceOverrideHint() {
      if (this.productHasVariants) {
        return 'Vazio = preço base. Aplica a todas as variações sem preço próprio.'
      }
      return 'Vazio = usar preço base do produto'
    },
    productNoDataText() {
      if (this.loadingProducts) return 'Carregando produtos com estoque…'
      if (this.productSearch?.trim()) {
        return 'Nenhum produto com estoque corresponde à busca'
      }
      return 'Nenhum produto com estoque disponível no grupo. Faça entrada no Estoque do grupo primeiro.'
    },
  },
  methods: {
    resetForm() {
      this.form = emptyForm(this.storeId)
      this.productSearch = ''
    },
    hydrateFromModel(model) {
      if (!model || (!model.id && !model.product_id)) {
        this.resetForm()
        return
      }
      this.form = {
        id: model.id || null,
        store_id: Number(this.storeId),
        product_id: model.product_id || model.product?.id || null,
        price_override:
          model.price_override === undefined || model.price_override === null
            ? null
            : model.price_override,
        active: model.active !== false,
        pdv_sort: model.pdv_sort != null ? model.pdv_sort : 0,
        initial_quantity: null,
        product: model.product || null,
      }
    },
    /**
     * Vuetify custom-filter: match name, SKU, stock label.
     * Signature: (value, query, item) where value is item-title.
     */
    filterProduct(_value, query, item) {
      const q = String(query || '')
        .trim()
        .toLowerCase()
      if (!q) return true
      const raw = item?.raw || item
      const hay = raw?.searchText || String(_value || '').toLowerCase()
      return hay.includes(q)
    },
    /**
     * Build picker from group stock balances (not full catalog).
     * Product eligible if any variant has group_available > 0.
     * Omitting itemsPerPage → BE returns full collection.
     */
    loadProducts() {
      const vm = this
      if (vm.loadingProducts) return Promise.resolve()
      vm.loadingProducts = true
      return api.stock
        .listGroupBalances({})
        .then((result) => {
          const { items } = normalizeListPayload(result)
          const map = {}
          const byProduct = {}
          for (const row of items || []) {
            const available = Number(
              row.group_available != null ? row.group_available : row.quantity
            )
            if (!Number.isFinite(available) || available <= 0) continue
            const productId = row.product_id || row.product?.id
            if (!productId) continue
            if (!map[productId]) {
              map[productId] = { qty: 0, variantCount: 0 }
            }
            map[productId].qty += available
            map[productId].variantCount += 1
            if (!byProduct[productId]) {
              const p = row.product
              byProduct[productId] = p
                ? { ...p, id: productId }
                : {
                    id: productId,
                    name: row.product_name || `Produto #${productId}`,
                    sku: null,
                    tracks_stock: true,
                    has_variants: false,
                  }
            }
            // mark multi-variant when more than one balance row
            if (map[productId].variantCount > 1) {
              byProduct[productId].has_variants = true
              byProduct[productId].variants_count = map[productId].variantCount
            }
          }
          vm.stockByProductId = map
          // Only stocked products; skip inactive if product payload present
          vm.products = Object.values(byProduct).filter(
            (p) => p.active !== false
          )
        })
        .catch((err) => {
          vm.stockByProductId = {}
          vm.products = []
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.loadingProducts = false
        })
    },
    buildPayload() {
      const f = this.form || {}
      const payload = {
        store_id: Number(this.storeId),
        product_id: f.product_id != null ? Number(f.product_id) : null,
        active: f.active !== false,
        pdv_sort: f.pdv_sort != null && f.pdv_sort !== '' ? Number(f.pdv_sort) : 0,
      }
      if (f.id) {
        payload.id = f.id
      }
      if (
        f.price_override === null ||
        f.price_override === undefined ||
        f.price_override === ''
      ) {
        payload.price_override = null
      } else {
        payload.price_override = Number(f.price_override)
      }
      return payload
    },
    submit() {
      const vm = this
      if (vm.requestingApi) return
      if (!vm.form?.product_id) {
        vm.$notify({ type: 'warn', text: 'Selecione um produto' })
        return
      }
      if (!vm.storeId) {
        vm.$notify({ type: 'warn', text: 'Loja inválida' })
        return
      }
      // Guard: create requires group stock (edit may keep zero-stock link)
      if (!vm.isEdit) {
        const s = vm.stockByProductId[vm.form.product_id]
        if (!s || !(s.qty > 0)) {
          vm.$notify({
            type: 'warn',
            text: 'Produto sem estoque disponível no grupo. Faça entrada no Estoque do grupo.',
          })
          return
        }
      }
      vm.requestingApi = true
      const wasEdit = vm.isEdit
      return api.storeProduct
        .updateOrCreate(vm.buildPayload())
        .then((result) => {
          if (wasEdit) {
            vm.$emit('updated', result)
            vm.$notify({ type: 'success', text: 'Produto da loja atualizado' })
          } else {
            vm.$emit('created', result)
            vm.$notify({ type: 'success', text: 'Produto vinculado à loja' })
          }
          vm.dialog = false
        })
        .catch((err) => {
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.requestingApi = false
        })
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal && (newVal.id || newVal.product_id) && !this.dialog) {
        this.hydrateFromModel(newVal)
        this.dialog = true
      }
    },
    dialog(newVal) {
      if (newVal) {
        if (this.modelValue && (this.modelValue.id || this.modelValue.product_id)) {
          this.hydrateFromModel(this.modelValue)
        } else if (!this.form?.product_id) {
          this.resetForm()
        }
        this.loadProducts()
      } else {
        this.resetForm()
        this.$emit('update:modelValue', {})
      }
    },
    storeId(newVal) {
      if (this.form) {
        this.form.store_id = newVal != null ? Number(newVal) : null
      }
    },
  },
}
</script>
